import type { ResearchRoomFocus, ResearchRoomMessage } from '@repo/contracts';

/**
 * Where on a screen the room's attention landed, worked out from the places
 * its lines were about — every spotlight the session raised, counted.
 *
 * Plain code over data the room already has: a mention is any question,
 * answer or poll placed on a spot; a complaint is an answer there that read
 * negative. Nothing is asked of a model and nothing new is stored.
 */

export interface HeatSpot {
  focus: ResearchRoomFocus;
  /** How many lines were about this spot. */
  mentions: number;
  /** How many of the answers here read negative. */
  complaints: number;
  /** 0–1, relative to the hottest spot on the screen. */
  heat: number;
}

/** Two places count as one spot when they are the same element — the same
 *  label at (nearly) the same place. */
function keyOf(focus: ResearchRoomFocus): string {
  return `${focus.label.toLowerCase()}@${String(Math.round(focus.x / 8))},${String(Math.round(focus.y / 8))}`;
}

export function heatSpots(messages: ResearchRoomMessage[]): HeatSpot[] {
  const spots = new Map<string, Omit<HeatSpot, 'heat'>>();
  for (const message of messages) {
    if (!message.focus || message.status === 'failed') continue;
    const key = keyOf(message.focus);
    const spot = spots.get(key) ?? {
      complaints: 0,
      focus: message.focus,
      mentions: 0,
    };
    spot.mentions += 1;
    if (message.kind === 'answer' && message.sentiment === 'Negative') {
      spot.complaints += 1;
    }
    spots.set(key, spot);
  }

  // A complaint weighs more than a mention: the heatmap is for finding what
  // is wrong, and a spot everyone praises should not outshine one that two
  // people struggled with.
  const weight = (s: Omit<HeatSpot, 'heat'>) => s.mentions + s.complaints;
  const hottest = Math.max(1, ...[...spots.values()].map(weight));
  return [...spots.values()]
    .map((spot) => ({ ...spot, heat: weight(spot) / hottest }))
    .sort((a, b) => a.heat - b.heat);
}
